<template>
  <div>
    <section id="priceHero" class="hero is-small">
      <div class="hero-body">
        <div class="container has-text-centered">
          <h1 class="title is-spaced">
            Pricing that <span>makes cents</span>.
          </h1>
          <p class="subtitle">
            Pay pennies on each order you ship, but save much more. Starting at $25 / month.
            <br>
            See how much Paccurate could save your business with the ROI calculator below.
          </p>
        </div>
      </div>
      <div id="tiers" class="hero-foot pricing-hero-foot">
        <div class="hero-foot-inner">
          <div class="container">
            <div class="columns has-text-centered">
              <div class="column tier">
                <span class="tierTitle">Cloud</span>
                <span class="tierCost"><sup>$</sup>25</span>
                <span class="tierDesc">Per month, minimum</span>
                <p>Our most popular plan. Great for companies shipping fewer than 60k orders per month.</p>
                <span class="metered">Includes 250 requests <span data-tooltip="After 250 requests, 5 cents per unique request, with volume discounts"><font-awesome-icon icon="question-circle" /></span></span>
                <a href="https://manage.paccurate.io/sign_up" class="button signupCTA is-primary bigButton">Get API Key</a>
              </div>
              <div class="column tier">
                <span class="tierTitle">Private Endpoint</span>
                <span class="tierCost"><sup>$</sup>499</span>
                <span class="tierDesc">Per month, minimum</span>
                <p>Dedicated Instance. Good for companies shipping more than 60k orders per month.</p>
                <span class="metered">Includes 10k requests <span data-tooltip="After 10k requests, 3 cents per unique request, with volume discounts"><font-awesome-icon icon="question-circle" /></span></span>
                <a href="https://paccurate.typeform.com/to/q8LTQqQq" class="button signupCTA talkCTA">Let's talk</a>
              </div>
              <div class="column tier">
                <span class="tierTitle">On-Premise</span>
                <span class="tierCost">Custom</span>
                <span class="tierDesc">Flexible Licenses</span>
                <p>When an extra milisecond is too long, host Paccurate in your own stack.</p>
                <a href="https://paccurate.typeform.com/to/q8LTQqQq" class="button signupCTA talkCTA">Let's talk</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    <roi />
    <section id="pricingOutline" class="hero is-small">
      <div class="hero-body">
        <div class="container has-text-centered">
          <h2 class="bigTitle">
            How Metered Pricing Works
          </h2>
          <p class="bigFollow">Our proposition is simple: Pay the equivalent of &lt; 1% of your shipping costs to save &gt; 10% overall.</p>
          <pac-columns>
            <div class="column has-text-left">
              <h3>After $25, only pay for what you use.</h3>
              <p>
                Your monthly bill is based on the number of unique API requests
                per month. Metered pricing starts at 5 cents per unique request.
              </p>
              <h3>Volume Discounts</h3>
              <p>The more you ship, the more you save. Use the estimator above to calculate your cost. Anticipate more than 100K shipments per month? <a href="https://paccurate.typeform.com/to/q8LTQqQq">Talk to Us</a></p>
            </div>
            <div class="column has-text-left">
              <h3>What's a <em>unique</em> request?</h3>
              <p>
                "Unique" means any request that seems to relate to a single order (matching SKUs, for example), even if rules or other factors are different.
              </p>
              <p>
                This flexibility allows you to cartonize for multiple carriers, with different rules, at multiple points in an order lifecycle. This can also make Paccurate more affordable than other cartonization solutions that charge you for every request.
              </p>
            </div>
          </pac-columns>
        </div>
      </div>
    </section>
    <recent-news :posts="posts" />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import Roi from '~/components/pricing/Roi.vue'
import RecentNews from '~/components/RecentNews.vue'

export default {
  components: {
    Roi,
    RecentNews
  },
  head () {
    return {
      title: 'Cartonization pricing that makes cents.'
    }
  },
  computed: {
    ...mapState([
      'posts'
    ])
  }
}
</script>

<style scoped>
/* Add this attribute to the element that needs a tooltip */
[data-tooltip] {
  position: relative;
  z-index: 2;
  cursor: pointer;
}

/* Hide the tooltip content by default */
[data-tooltip]:before,
[data-tooltip]:after {
  visibility: hidden;
  -ms-filter: "progid:DXImageTransform.Microsoft.Alpha(Opacity=0)";
  filter: progid: DXImageTransform.Microsoft.Alpha(Opacity=0);
  opacity: 0;
  pointer-events: none;
}

/* Position tooltip above the element */
[data-tooltip]:before {
  position: absolute;
  bottom: 150%;
  left: 50%;
  margin-bottom: 5px;
  margin-left: -80px;
  padding: 7px;
  width: 160px;
  -webkit-border-radius: 3px;
  -moz-border-radius: 3px;
  border-radius: 3px;
  background-color: #000;
  background-color: hsla(0, 0%, 20%, 0.9);
  color: #fff;
  content: attr(data-tooltip);
  text-align: center;
  font-size: 14px;
  line-height: 1.2;
  font-weight: 400;
}

/* Triangle hack to make tooltip look like a speech bubble */
[data-tooltip]:after {
  position: absolute;
  bottom: 150%;
  left: 50%;
  margin-left: -5px;
  width: 0;
  border-top: 5px solid #000;
  border-top: 5px solid hsla(0, 0%, 20%, 0.9);
  border-right: 5px solid transparent;
  border-left: 5px solid transparent;
  content: " ";
  font-size: 0;
  line-height: 0;
}

/* Show tooltip content on hover */
[data-tooltip]:hover:before,
[data-tooltip]:hover:after {
  visibility: visible;
  -ms-filter: "progid:DXImageTransform.Microsoft.Alpha(Opacity=100)";
  filter: progid: DXImageTransform.Microsoft.Alpha(Opacity=100);
  opacity: 1;
}
</style>
